import React, { useState, useMemo } from 'react';
import { Network, Plus, Trash2, Target, Lightbulb } from 'lucide-react';
import { Card, Input, Button } from '../ui';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

interface Skill {
  id: string;
  name: string;
  desiredScore: number;
  currentScore: number;
}

const DEFAULT_SKILLS: Skill[] = [
  { id: '1', name: 'Finance / CPA', desiredScore: 5, currentScore: 2 },
  { id: '2', name: 'Legal / Risk', desiredScore: 4, currentScore: 4 },
  { id: '3', name: 'Marketing / PR', desiredScore: 4, currentScore: 1 },
  { id: '4', name: 'Fundraising', desiredScore: 5, currentScore: 3 },
  { id: '5', name: 'Tech / IT', desiredScore: 3, currentScore: 1 },
  { id: '6', name: 'HR / Talent', desiredScore: 3, currentScore: 2 }
];

export function BoardSkillGapAnalyzer() {
  const [skills, setSkills] = useState<Skill[]>(DEFAULT_SKILLS);
  const [newSkillName, setNewSkillName] = useState('');

  const handleAddSkill = () => {
    if (!newSkillName.trim()) return;
    setSkills([...skills, {
      id: Date.now().toString(),
      name: newSkillName.trim(),
      desiredScore: 5,
      currentScore: 0
    }]);
    setNewSkillName('');
  };

  const handleRemoveSkill = (id: string) => {
    setSkills(skills.filter(s => s.id !== id));
  };

  const updateSkill = (id: string, field: 'desiredScore' | 'currentScore', value: number) => {
    setSkills(skills.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const model = useMemo(() => {
    const chartData = skills.map(skill => {
      const gap = Math.max(0, skill.desiredScore - skill.currentScore);

      return {
        subject: skill.name,
        Desired: skill.desiredScore,
        Current: skill.currentScore,
        gap
      };
    });

    const totalGap = chartData.reduce((sum, d) => sum + d.gap, 0);
    const averageGap = skills.length > 0 ? (totalGap / skills.length) : 0;
    const isHealthy = averageGap < 1;

    const maxGapSkill = skills.reduce<((typeof skills)[number]) | undefined>((best, skill) => {
      const gap = Math.max(0, skill.desiredScore - skill.currentScore);
      if (!best || gap > Math.max(0, best.desiredScore - best.currentScore)) return skill;
      return best;
    }, skills[0]);

    return {
      chartData,
      totalGap,
      maxGapSkill,
      isHealthy
    };
  }, [skills]);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-violet-800 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Network className="w-6 h-6 mr-3 text-violet-300" />
            Board Skill Gap Analyzer
          </h2>
          <p className="text-violet-100 max-w-xl">
            Visualize your board of directors' competencies. Identify critical skill shortages to guide your next nomination and recruitment cycle.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
            <h3 className="font-bold flex items-center">
              <Target className="w-4 h-4 mr-2 text-slate-500" /> Competency Matrix (0-5 Scale)
            </h3>
          </div>
          
          <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[500px] overflow-y-auto p-4 space-y-4">
            {skills.map(skill => (
              <div key={skill.id} className="pt-4 first:pt-0">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-bold text-slate-900 dark:text-white">{skill.name}</div>
                  <button onClick={() => handleRemoveSkill(skill.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1 font-bold uppercase">
                      <span>Current</span>
                      <span>{skill.currentScore}</span>
                    </div>
                    <input 
                      type="range" min="0" max="5" step="1"
                      value={skill.currentScore}
                      onChange={(e) => updateSkill(skill.id, 'currentScore', Number(e.target.value))}
                      className="w-full accent-indigo-500"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1 font-bold uppercase">
                      <span>Desired</span>
                      <span>{skill.desiredScore}</span>
                    </div>
                    <input 
                      type="range" min="0" max="5" step="1"
                      value={skill.desiredScore}
                      onChange={(e) => updateSkill(skill.id, 'desiredScore', Number(e.target.value))}
                      className="w-full accent-violet-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex gap-2">
            <Input 
              placeholder="New skill (e.g. Cybersecurity)" 
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSkill()}
            />
            <Button onClick={handleAddSkill} className="shrink-0 bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Overall Board Health</h3>
              <div className={`text-2xl font-black ${model.isHealthy ? 'text-emerald-500' : 'text-amber-500'}`}>
                {model.isHealthy ? 'Well Rounded' : 'Skill Gaps Detected'}
              </div>
            </Card>
            
            <Card className="p-6 border-violet-200 dark:border-violet-900 shadow-xl bg-violet-50 dark:bg-violet-900/10">
              <h3 className="text-xs font-bold text-violet-700 dark:text-violet-400 uppercase tracking-wider mb-2">Priority Recruitment Target</h3>
              <div className="text-2xl font-black text-violet-900 dark:text-violet-100 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-violet-500" />
                {model.maxGapSkill ? model.maxGapSkill.name : 'N/A'}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 h-[450px] flex flex-col">
            <h3 className="text-lg font-bold mb-2">Competency Radar</h3>
            <p className="text-sm text-slate-500 mb-4">Blue area represents current board coverage. The gray outline is the desired target.</p>
            
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={model.chartData}>
                  <PolarGrid stroke="#334155" opacity={0.2} />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 12, fontWeight: 'bold' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} axisLine={false} />
                  
                  <Radar
                    name="Desired Target"
                    dataKey="Desired"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    fill="#94a3b8"
                    fillOpacity={0.1}
                  />
                  <Radar
                    name="Current Coverage"
                    dataKey="Current"
                    stroke="#4f46e5"
                    strokeWidth={3}
                    fill="#6366f1"
                    fillOpacity={0.5}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
